<script>
  import Header from "@components/presence-calendar/header.svelte";
  import Slot from "@components/presence-calendar/slot.svelte";
  import DateCell from "@components/presence-calendar/date-cell.svelte";
  import DutyShiftCell from "@components/presence-calendar/duty-shift-cell.svelte";
  import SelectedPresenceModal from "@components/presence-calendar/selected-presence-modal.svelte";
  import SelectedAbsenceModal from "@components/presence-calendar/selected-absence-modal.svelte";
  import DutyShiftModal from "@components/presence-calendar/duty-shift-modal.svelte";
  import ClinicOpeningCell from "@components/presence-calendar/clinic-opening-cell.svelte";
  import ClinicOpeningModal from "@components/presence-calendar/clinic-opening-modal.svelte";
  import SummaryPanel from "@components/presence-calendar/summary-panel.svelte";
  import { get, post, patch, destroy } from "@lib/requests";
  import { showAlert } from "@lib/utils";

  let { clinic, month, users, user, dates, role, userType } = $props();

  let loading = $state(false);

  let presences = $state([]);
  let absences = $state([]);
  let dutyShifts = $state([]);
  let clinicOpenings = $state([]);

  let shownUsers = $state(user ? [user] : users);
  let sortedUsers = $derived([...shownUsers].sort((a, b) => a.name.localeCompare(b.name)));

  let absenceMode = $state(false);
  let halfDayMode = $state(false);
  let showPresences = $state(true);
  let showAbsences = $state(true);

  let alerts = $state(false);

  // The summary panel stays open between visits (per browser)
  const SUMMARY_KEY = "presence-calendar-summary-open";
  let summaryOpen = $state(readSummaryOpen());

  function readSummaryOpen() {
    try { return localStorage.getItem(SUMMARY_KEY) === "1"; } catch { return false; }
  }

  $effect(() => {
    try { localStorage.setItem(SUMMARY_KEY, summaryOpen ? "1" : "0"); } catch {}
  });

  let presencesIndex = $derived(`/api/v1/user_presences?clinic=${clinic.id}&date=${month.toISOString()}`)
  let absencesIndex = $derived(`/api/v1/user_absences?clinic=${clinic.id}&date=${month.toISOString()}`)
  let dutyShiftsIndex = $derived(`/api/v1/duty_shifts?date=${month.toISOString()}`)
  let clinicOpeningsIndex = $derived(`/api/v1/clinic_openings?clinic=${clinic.id}&date=${month.toISOString()}`)
  let sectionClass = $derived(loading ? "opacity-50 pointer-events-none" : "");

  let shownPresence = $state(null);
  let shownAbsence = $state(null);
  let dutyShiftDate = $state(null);
  let clinicOpeningDate = $state(null);

  $effect(fetchData);

  async function fetchData() {
    loading = true;
    presences = [];
    absences = [];
    clinicOpenings = [];

    try {
      const [presencesResponse, absencesResponse, dutyShiftsResponse, clinicOpeningsResponse] = await Promise.all([
        get(presencesIndex),
        get(absencesIndex),
        get(dutyShiftsIndex),
        get(clinicOpeningsIndex)
      ]);

      const [presencesData, absencesData, dutyShiftsData, clinicOpeningsData] = await Promise.all([
        presencesResponse.json,
        absencesResponse.json,
        dutyShiftsResponse.json,
        clinicOpeningsResponse.json
      ]);

      if (presencesResponse.ok && absencesResponse.ok && dutyShiftsResponse.ok && clinicOpeningsResponse.ok) {
        presences = presencesData;
        absences = absencesData;
        dutyShifts = dutyShiftsData;
        clinicOpenings = clinicOpeningsData;
      } else {
        showAlert("Une erreur est survenue lors de la récupération des données", { type: "error" });
      }
    } catch (error) {
      showAlert("Une erreur est survenue lors de la récupération des données", { type: "error" });
    } finally {
      loading = false;
    }
  }

  function findPresences(user, date) {
    if (!showPresences) return [];
    return presences.filter((presence) => {
      const dateString = date.toISOString().split("T")[0];
      return user.id === presence.user.id && dateString === presence.date;
    });
  }

  function findAbsences(user, date) {
    if (!showAbsences) return [];
    return absences.filter((absence) => {
      const dateString = date.toISOString().split("T")[0];
      return user.id === absence.user.id && dateString === absence.date;
    });
  }

  function findDutyVets(date) {
    const shifts = dutyShifts.filter((shift) => shift.date === date.toISOString().split("T")[0]);
    return shifts.flatMap((shift) => shift.vets);
  }

  function findClinicOpening(date) {
    const dateString = date.toISOString().split("T")[0];
    return clinicOpenings.find((opening) => opening.date === dateString);
  }

  function toggleUser(user) {
    const url = new URL(window.location.href);
    const userShown = shownUsers.length === 1 && shownUsers[0].id === user.id;

    if (userShown) {
      shownUsers = users;
      url.searchParams.delete("user");
    } else {
      shownUsers = [user]
      url.searchParams.set("user", user.id);
    }
    window.history.pushState({}, "", url.toString());
  }

  async function createPresence(params = {}) {
    const response = await post("/api/v1/user_presences", { body: params });
    const data = await response.json;
    if (response.ok) {
      const newPresence = data.presence;
      presences = [...presences, newPresence];
      if (alerts) showAlert("Présence ajoutée", { type: "success" });
    } else {
      showAlert(data.error || "Erreur lors de la création de la présence", { type: "error" });
    }
  }

  async function updatePresence(id, params = {}) {
    const response = await patch(`/api/v1/user_presences/${id}`, { body: params });
    const data = await response.json;
    if (response.ok) {
      let presence = presences.find((presence) => presence.id === id);
      presence.notes = data.presence.notes;
      presence.period = data.presence.period;
      presence.hours = data.presence.hours;
      showAlert("Présence mise à jour avec succès", { type: "success" });
    } else {
      showAlert(data.error || "Erreur lors de la mise à jour de la présence", { type: "error" });
    }
  }

  async function deletePresence(id) {
    const response = await destroy(`/api/v1/user_presences/${id}`);
    const data = await response.json;
    if (!response.ok) return showAlert(data.error, { type: "error" });
    presences = presences.filter((presence) => presence.id !== id);
    showAlert("Présence supprimée", { type: "success" });
  }

  async function createAbsence(params = {}) {
    const response = await post("/api/v1/user_absences", { body: params });
    const data = await response.json;
    if (response.ok) {
      const newAbsence = data.absence;
      absences = [...absences, newAbsence];
      if (alerts) showAlert("Absence ajoutée", { type: "success" });
    } else {
      showAlert(data.error || "Erreur lors de la création de l'absence", { type: "error" });
    }
  }

  async function updateAbsence(id, params = {}) {
    const response = await patch(`/api/v1/user_absences/${id}`, { body: params });
    const data = await response.json;
    if (response.ok) {
      let absence = absences.find((absence) => absence.id === id);
      absence.reason = data.absence.reason;
      absence.period = data.absence.period;
      showAlert("Absence mise à jour avec succès", { type: "success" });
    } else {
      showAlert(data.error || "Erreur lors de la mise à jour de l'absence", { type: "error" });
    }
  }

  async function deleteAbsence(id) {
    const response = await destroy(`/api/v1/user_absences/${id}`);
    const data = await response.json;
    if (!response.ok) return showAlert(data.error, { type: "error" });
    absences = absences.filter((absence) => absence.id !== id);
    showAlert("Absence supprimée", { type: "success" });
  }

  async function updateDutyShift(selectedIds, notes) {
    const dateString = dutyShiftDate.toISOString().split("T")[0];
    const response = await post(`/api/v1/duty_shifts/assign?date=${dateString}`, { body: { vet_ids: selectedIds, notes } });
    const data = await response.json;
    if (!response.ok) return showAlert(data.error, { type: "error" });
    const filteredShifts = dutyShifts.filter((shift) => shift.date !== dateString);
    if (data.id) {
      dutyShifts = [...filteredShifts, data];
    } else {
      dutyShifts = filteredShifts;
    }
    dutyShiftDate = null;
  }

  async function updateClinicOpening(userId, notes) {
    const dateString = clinicOpeningDate.toISOString().split("T")[0];
    const response = await post("/api/v1/clinic_openings/assign", {
      body: { clinic_id: clinic.id, date: dateString, user_id: userId, notes }
    });
    const data = await response.json;

    if (!response.ok) return showAlert(data.error, { type: "error" });

    const filteredOpenings = clinicOpenings.filter((opening) => opening.date !== dateString);
    if (data.clinic_opening) {
      clinicOpenings = [...filteredOpenings, data.clinic_opening];
    } else {
      clinicOpenings = filteredOpenings;
    }

    clinicOpeningDate = null;
    showAlert("Ouverture mise à jour", { type: "success" });
  }

  function handleKeydown(e) {
    absenceMode = e.metaKey || e.ctrlKey;
    halfDayMode = e.shiftKey;
  }

  function handleKeyup(e) {
    if (["Meta", "Control"].includes(e.key)) {
      absenceMode = false;
    } else if (e.key === "Shift") {
      halfDayMode = false;
    }
  }

</script>

<svelte:window onkeydown={handleKeydown} onkeyup={handleKeyup}/>

<Header {loading} bind:absenceMode bind:halfDayMode bind:showPresences bind:showAbsences {role} />

{#if loading}
  <section class="flex items-center justify-center bg-gray-100/70" style="height: calc(100vh - 20rem);">
    <p class="text-gray-600 text-sm">Chargement des données...</p>
  </section>
{:else}

  <!-- The calendar, with the summary panel beside it when open -->
  <div class="flex items-start gap-4">

  <!-- A container for the dates -->
  <section class={`presence-calendar flex-1 min-w-0 ${sectionClass}`}>

    <!-- The header area -->
    <header class="flex justify-between items-center mb-0 sticky top-0 z-10 bg-white">

      <!-- An empty cell showing the month short name -->
      <div class="presence-cell w-20 shrink-0 uppercase">{month.toLocaleString('fr', { month: 'short' })}</div>

      <!-- Each user -->
      {#each sortedUsers as user (user.id)}
        <div class={`presence-cell relative flex gap-2 items-center bg-${user.color}/10 text-gray-800`}>
          <button type="button" class="hover:underline" onclick={() => toggleUser(user)}>{user.name}</button>
        </div>
      {:else}
        <div class="presence-cell">Aucun utilisateur</div>
      {/each}

      <!-- A column for duty shifts -->
      <!-- {#if showDutyShifts}
        <div class="presence-cell bg-yellow-100 text-yellow-800">Garde</div>
      {/if} -->

      {#if userType === "vet"}
        <div class="presence-cell bg-yellow-100 text-yellow-800">Garde</div>
      {:else if userType === "aux"}
        <div class="presence-cell bg-yellow-100 text-yellow-800">Ouverture</div>
      {/if}

    </header>

    <!-- The dates area -->

    <!-- Wrapper div for each row -->
    {#each dates as date (date)}
      <div class="flex">

        <!-- The displayed date -->
        <DateCell {date} />

        <!-- Then loop through each user -->
        {#each sortedUsers as user (user.id)}
          <Slot {user} {date} {clinic} {alerts} {role}
            presences={findPresences(user, date)}
            absences={findAbsences(user, date)}
            {createPresence} {updatePresence} {deletePresence} {createAbsence} {updateAbsence} {deleteAbsence}
            bind:shownPresence
            bind:shownAbsence
            {absenceMode} {halfDayMode} />
        {:else}
          <div class="presence-cell"></div>
        {/each}

        <!-- A column for duty shifts -->
        <!-- {#if showDutyShifts}
          <DutyShiftCell {date} vets={findDutyVets(date)} {role} onSelect={() => dutyShiftDate = date} />
        {/if} -->

        {#if userType === "vet"}
          <DutyShiftCell {date} vets={findDutyVets(date)} {role} onSelect={() => dutyShiftDate = date} />
        {:else if userType === "aux"}
          <ClinicOpeningCell {date} opening={findClinicOpening(date)} {role} onSelect={() => clinicOpeningDate = date} />
        {/if}

      </div>
    {/each}
  </section>

  {#if summaryOpen}
    <SummaryPanel users={sortedUsers} {clinic} {month} {dates} {userType} {presences} {absences} {dutyShifts} {clinicOpenings} onClose={() => summaryOpen = false} />
  {/if}

  </div>

  {#if !summaryOpen}
    <button type="button" class="fixed bottom-6 right-6 z-20 flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-smaller text-gray-700 shadow-lg hover:bg-gray-50" onclick={() => summaryOpen = true}>
      <i class="fal fa-chart-bar"></i>
      <span>Résumé</span>
    </button>
  {/if}

  <SelectedPresenceModal bind:presence={shownPresence} {role} onUpdate={updatePresence} onDelete={deletePresence} />
  <SelectedAbsenceModal bind:absence={shownAbsence} {role} onUpdate={updateAbsence} onDelete={deleteAbsence} />
  <DutyShiftModal bind:date={dutyShiftDate} onUpdate={updateDutyShift} />
  <ClinicOpeningModal bind:date={clinicOpeningDate} {clinic} onUpdate={updateClinicOpening} />
{/if}
